import { useState, useEffect, useContext } from 'react';
import { sum } from 'lodash';
import { Product, ProductOrder, Material } from '../../../../data/entities';
import { EnhancedProduct, EnhancedMaterial } from '../types';
import AddProductModal from '../AddProductModal';
import { ContentWrapper, ProductButtonRow, ProductTableText } from '../Styled';
import { SummaryTableCell, SummaryTableRow } from '../AddProductModal/Styled';
import CustomTable from '../../../../shared/components/CustomTable';
import { ProductOrderProductTableColumns, OpenStateProductTableColumns } from '../../../../configs/productOrder';
import { StyledButton } from '../../Styled';
import { IconButton, TableRow } from '@mui/material';
import { StyledTableCell } from '../../../../shared/components/CustomTable/Styled';
import TextBox from '../../../../shared/components/TextBox';
import DeleteIcon from '../../../../shared/svg/DeleteIcon';
import { color } from '../../../../shared/styles';
import { WarningMessage, TagName } from './Styled';
import WarningTableIcon from '../../../../shared/svg/WarningTableIcon';
import { formatCurrency } from '../../../../utils/format';
import { StyledNavLink } from '../../../../shared/styled/TableStyles';
import { AuthenticationContext } from '../../../../provider/AuthenticationProvider';
import { ROLE } from '../../../../data/enums';

interface ProductRowProps {
    item: any;
    extraRowProps: any;
}

const getSelectedQuantity = (item: any) => {
    return item.selectedQuantity !== undefined ? item.selectedQuantity : 0;
};

const getMissingQuantity = (item: any) => {
    return Math.abs(Math.min(0, item.quantity - getSelectedQuantity(item)));
};

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const quantity = getSelectedQuantity(item);
    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);
    const orderState = extraRowProps?.orderState || 'Open';
    const isOpenState = orderState === 'Open';

    const isQuantitySufficient = item.missingQuantity === 0 || getMissingQuantity(item) === 0;
    const backgroundColor = isQuantitySufficient ? '' : color.status.DANGER_LIGHT;
    const selectedQuantityBorderColor = isQuantitySufficient ? '' : color.status.FAIL;

    const percentDiscount = item.discount !== undefined ? item.discount : 0;
    const percentMarkup = item.markup !== undefined ? item.markup : 0;

    const price = item.item_type === 'material' 
        ? (item.material_price !== undefined ? item.material_price : 0)
        : (item.product_price !== undefined ? item.product_price : 0);

    const updateMaterialPrice = (value: string) => {
        if (extraRowProps && extraRowProps.updateMaterialPrice) {
            extraRowProps.updateMaterialPrice(item.id, Number(value));
        }
    };

    return (
        <>
            {!item.isSummary ? (
                <TableRow sx={{ backgroundColor }}>
                    <StyledTableCell align="left">
                        <StyledNavLink to={`/${item.item_type === 'material' ? 'materials' : 'products'}/${item.id}`}>
                            {item.product_code || item.material_code ? item.product_code || item.material_code : item.id}
                        </StyledNavLink>
                    </StyledTableCell>
                    <StyledTableCell align="left">
                        {item.name}
                        {item.item_type && (
                            <TagName itemType={item.item_type}>
                                {item.item_type === 'product' ? 'Sản phẩm' : 'Vật tư'}
                            </TagName>
                        )}
                    </StyledTableCell>
                    
                    {canSeePrice && (
                        <StyledTableCell align="left">
                            {item.item_type === 'material' ? (
                                !extraRowProps.isView ? (
                                    <TextBox
                                    style={{
                                        borderColor: selectedQuantityBorderColor,
                                        backgroundColor,
                                        width: '160px',
                                        minWidth: '160px',
                                }}
                                text={`${(price || 0).toString()}`}
                                onChangeText={updateMaterialPrice}
                                disabled={extraRowProps.isView}
                            />) : (
                                formatCurrency(price)
                            )
                        ) : (
                            formatCurrency(price)
                            )}
                        </StyledTableCell>
                    )}
                    
                    {canSeePrice && !isOpenState && (
                        <StyledTableCell align="left">
                            <TextBox
                                style={{
                                    borderColor: selectedQuantityBorderColor,
                                    backgroundColor,
                                }}
                                text={`${(percentDiscount || 0).toString()}`}
                                onChangeText={(value: string) => extraRowProps.onChangeProductDiscount(item.id, Number(value))}
                                disabled={extraRowProps.isView}
                            />
                        </StyledTableCell>
                    )}
                    
                    {canSeePrice && !isOpenState && (
                        <StyledTableCell align="left">
                                <TextBox
                            style={{
                                borderColor: selectedQuantityBorderColor,
                                backgroundColor,
                            }}
                            text={`${(percentMarkup || 0).toString()}`}
                            onChangeText={(value: string) => extraRowProps.onChangeProductMarkup(item.id, Number(value))}
                                disabled={extraRowProps.isView}
                            />
                        </StyledTableCell>
                    )}

                    {canSeePrice && !isOpenState && (
                        <StyledTableCell align="left">
                            {formatCurrency(
                                Math.ceil(((price * (100 - percentDiscount)) / 100) * ((100 + percentMarkup) / 100))
                            )}
                        </StyledTableCell>
                    )}

                    <StyledTableCell align="left">{item.quantity}</StyledTableCell>
                    <StyledTableCell align="left">
                        <TextBox
                            style={{
                                borderColor: selectedQuantityBorderColor,
                                backgroundColor,
                            }}
                            text={quantity.toString()}
                            onChangeText={(value: string) =>
                                extraRowProps.onChangeProductSelectedQuantity(item.id, Number(value))
                            }
                            disabled={extraRowProps.isView}
                        />
                    </StyledTableCell>

                    {extraRowProps.isView && (
                        <StyledTableCell align="left">
                            {item.missingQuantity !== undefined ? item.missingQuantity : getMissingQuantity(item)}
                        </StyledTableCell>
                    )}

                    <StyledTableCell align="left">
                        {!extraRowProps.isView && (
                            <IconButton onClick={() => extraRowProps.removeItem(item)}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </StyledTableCell>
                </TableRow>
            ) : (
                <SummaryTableRow>
                    <SummaryTableCell align="left">{item.id}</SummaryTableCell>
                    <SummaryTableCell align="left">{item.name}</SummaryTableCell>
                    {canSeePrice && (
                        <SummaryTableCell align="left">{formatCurrency(item.totalOriginalPrice)}</SummaryTableCell>
                    )}
                    {canSeePrice && !isOpenState && (
                        <SummaryTableCell align="left"></SummaryTableCell>
                    )}
                    {canSeePrice && !isOpenState && (
                        <SummaryTableCell align="left"></SummaryTableCell>
                    )}
                    {canSeePrice && !isOpenState && (
                        <SummaryTableCell align="left">{formatCurrency(item.totalDiscountPrice)}</SummaryTableCell>
                    )}
                    <SummaryTableCell align="left"></SummaryTableCell>
                    <SummaryTableCell align="left"></SummaryTableCell>
                    {extraRowProps.isView && <SummaryTableCell align="left"></SummaryTableCell>}
                    <SummaryTableCell align="left"></SummaryTableCell>
                </SummaryTableRow>
            )}
        </>
    );
};

interface ProductListProps {
    productOrder: ProductOrder;
    mergeProductOrder: Function;
    isView?: boolean;
    orderState?: string;
}

const ProductList = ({ productOrder, mergeProductOrder, isView, orderState = 'Open' }: ProductListProps) => {
    const { products = [], materials = [] } = productOrder;
    const [isProductModalOpen, setProductModalOpen] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const customer = productOrder.customer;
    const customerCategory = customer?.category;

    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);
    const isOpenState = orderState === 'Open';

    const enhancedProducts = products as EnhancedProduct[];
    
    const enhancedMaterials = materials.map((material : any) => ({
        id: material.id,
        is_active: material.is_active,
        name: material.name,
        price: material.price,
        quantity: material.quantity,
        selectedQuantity: material.selectedQuantity,
        missingQuantity: material.missingQuantity,
        material_code: material.material_code,
        item_type: 'material' as const,
        material_price: material.material_price,
        discount: material.discount,
        markup: material.markup,
    })) as EnhancedMaterial[];
    
    const allItems = [...enhancedProducts, ...enhancedMaterials] as any[];

    const productCount = enhancedProducts.length;
    const materialCount = enhancedMaterials.length;

    const summaryProduct = {
        id: `Tổng ${allItems.length} mục`,
        name: 'Subtotal',
        totalDiscountPrice: sum(
            allItems.map(
                (item: any) => {
                    const itemPrice = item.item_type === 'material' ? item.material_price : item.product_price;
                    const discount = item.discount || 0;
                    const markup = item.markup || 0;
                    return getSelectedQuantity(item) *
                    Math.ceil(
                        ((itemPrice * (100 - discount)) / 100) * ((100 + markup) / 100)
                    );
                }
            )
        ),
        totalOriginalPrice: sum(
            allItems.map((item: any) => {
                const itemPrice = item.item_type === 'material' ? item.material_price : item.product_price;
                return getSelectedQuantity(item) * itemPrice;
            })
        ),
        isSummary: true,
    };

    const tableItems = allItems.length === 0 ? [] : [...allItems, summaryProduct];

    const isSufficient = tableItems.every(
        (item: any) => item.isSummary || item.missingQuantity === 0 || getMissingQuantity(item) === 0
    );

    const onRemoveProduct = (item: EnhancedProduct) => {
        if (item.item_type === 'material') {
            const removedMaterials = enhancedMaterials.filter((m: any) =>
                !(m.id === item.id && m.item_type === item.item_type)
            );
            mergeProductOrder({
                materials: removedMaterials,
                products: enhancedProducts
            });
        } else {
            const removedProducts = enhancedProducts.filter((p: any) =>
                !(p.id === item.id && p.item_type === item.item_type)
            );
            mergeProductOrder({
                products: removedProducts,
                materials: enhancedMaterials
            });
        }
    };

    const onChangeProductDiscount = (id: string, discount: any) => {
        const isMaterial = enhancedMaterials.some(item => item.id === id);
        
        if (isMaterial) {
            const updatedMaterials = enhancedMaterials.map((material: any) => {
                if (material.id === id) {
                    return {
                        ...material,
                        discount,
                    };
                }
                return material;
            });
            mergeProductOrder({ 
                materials: updatedMaterials,
                products: enhancedProducts
            });
        } else {
            const updatedProducts = enhancedProducts.map((product: any) => {
                if (product.id === id) {
                    return {
                        ...product,
                        discount,
                    };
                }
                return product;
            });
            mergeProductOrder({ 
                products: updatedProducts,
                materials: enhancedMaterials
            });
        }
    };

    const onChangeProductMarkup = (id: string, markup: any) => {
        const isMaterial = enhancedMaterials.some(item => item.id === id);
        
        if (isMaterial) {
            const updatedMaterials = enhancedMaterials.map((material: any) => {
                if (material.id === id) {
                    return {
                        ...material,
                        markup,
                    };
                }
                return material;
            });
            mergeProductOrder({ 
                materials: updatedMaterials,
                products: enhancedProducts
            });
        } else {
            const updatedProducts = enhancedProducts.map((product: any) => {
                if (product.id === id) {
                    return {
                        ...product,
                        markup,
                    };
                }
                return product;
            });
            mergeProductOrder({ 
                products: updatedProducts,
                materials: enhancedMaterials
            });
        }
    };

    const onChangeProductSelectedQuantity = (id: string, quantity: any) => {
        // Check if it's a material or product
        const isMaterial = enhancedMaterials.some(item => item.id === id);

        if (isMaterial) {
            const updatedMaterials = enhancedMaterials.map((material: any) => {
                if (material.id === id) {
                    const missingQuantity = Math.abs(Math.min(0, material.quantity - quantity));

                    return {
                        ...material,
                        selectedQuantity: quantity,
                        missingQuantity,
                    };
                }
                return material;
            });
            mergeProductOrder({
                materials: updatedMaterials,
                products: enhancedProducts
            });
        } else {
            const updatedProducts = enhancedProducts.map((product: any) => {
                if (product.id === id) {
                    const missingQuantity = Math.abs(Math.min(0, product.quantity - quantity));

                    return {
                        ...product,
                        selectedQuantity: quantity,
                        missingQuantity,
                    };
                }
                return product;
            });
            mergeProductOrder({
                products: updatedProducts,
                materials: enhancedMaterials
            });
        }
    };

    const updateMaterialPrice = (id: string, materialPrice: number) => {
        const updatedMaterials = enhancedMaterials.map((material: any) => {
            if (material.id === id) {
                return {
                    ...material,
                    material_price: materialPrice,
                };
            }
            return material;
        });
        
        mergeProductOrder({ 
            materials: updatedMaterials,
            products: enhancedProducts
        });
    };

    const getColumns = () => {
        const isOpenState = orderState === 'Open';
        const baseColumns = isOpenState ? OpenStateProductTableColumns : ProductOrderProductTableColumns;
        
        const columns = isView
            ? baseColumns
            : baseColumns.filter((item) => item.text !== 'Số lượng thiếu');
        
        if (!canSeePrice) {
            return columns.filter((item) => 
                item.text !== 'Giá bán' && 
                item.text !== 'Chiết khấu (%)' && 
                item.text !== 'Thuế suất (%)' && 
                item.text !== 'Giá sau chiết khấu'
            );
        }

        return columns;
    };

    return (
        <>
            {isProductModalOpen && !isView && (
                <AddProductModal
                    selectedProducts={[...enhancedProducts, ...enhancedMaterials] as any[]}
                    open={isProductModalOpen}
                    handleClose={() => setProductModalOpen(false)}
                    mergeProductOrder={mergeProductOrder}
                    onChangeProductSelectedQuantity={onChangeProductSelectedQuantity}
                    isView={isView}
                    customerId={customer?.id}
                    customerCategory={customerCategory}
                    enforceCommonCategory={productOrder.order_type !== "RESERVE"}
                    orderState={orderState}
                />
            )}

            <ContentWrapper>
                <ProductTableText>
                    Danh sách mục{' '}
                    <span
                        style={{
                            color: color.secondary.DARK_GREY,
                            fontSize: '16px',
                            fontWeight: '400',
                        }}
                    >
                        ({productCount} sản phẩm, {materialCount} vật tư)
                    </span>
                </ProductTableText>

                {!isSufficient && (
                    <WarningMessage>
                        <WarningTableIcon />
                        <span>
                            <span style={{ fontWeight: '700' }}>Lưu ý quan trọng: </span>
                            Một số sản phẩm trong danh sách vượt quá tồn kho cho phép. Bạn cần bổ sung số lượng sau khi tạo
                            đơn hàng.
                        </span>
                    </WarningMessage>
                )}

                <CustomTable
                    itemList={tableItems}
                    RowElement={ProductRow}
                    TableColumns={getColumns()}
                    extraRowProps={{
                        removeItem: onRemoveProduct,
                        onChangeProductSelectedQuantity,
                        onChangeProductDiscount,
                        onChangeProductMarkup,
                        updateMaterialPrice,
                        isView,
                        orderState,
                    }}
                    containerStyle={{ maxHeight: '400px' }}
                />

                {allItems.length === 0 && 'Không có mục được chọn'}
                {!isView && (
                    <ProductButtonRow>
                        <StyledButton variant="contained" onClick={() => setProductModalOpen(true)}>
                            Thêm mục
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};

export default ProductList;
